import { useEffect, useState, useMemo } from "react";
import { Phone, ArrowRight, Facebook } from "lucide-react";
import { useContent } from "@/context/ContentContext";
import { detectVideoKind, youtubeEmbedUrl } from "@/lib/media";

const DEFAULT_HERO_BG = "https://images.unsplash.com/photo-1504328345606-18bbc8c9d7d1";
const SLIDE_INTERVAL_MS = 6000;

export default function Hero() {
  const { content, loading } = useContent();
  const hero = content?.hero || {};
  const contact = content?.contact || {};
  const phone = contact.phone || "(254) 833-7563";
  const phoneLink = contact.phone_link || "+12548337563";
  const facebookUrl = (contact.facebook_url || "").trim();

  // Derived media source
  const videoKind = useMemo(() => (hero.video_url ? detectVideoKind(hero.video_url) : null), [hero.video_url]);

  const slides = useMemo(() => {
    if (Array.isArray(hero.slides) && hero.slides.length) return hero.slides;
    // Legacy single-image fallback
    if (hero.image_url || hero.image_data) return [{ image_url: hero.image_url, image_data: hero.image_data }];
    // Only fall back to the stock image once content has actually loaded and
    // returned zero slides. While `loading` is true we render nothing (dark
    // placeholder) so visitors never see a random Unsplash photo flash.
    return loading ? [] : [{ image_url: DEFAULT_HERO_BG }];
  }, [hero.slides, hero.image_url, hero.image_data, loading]);

  const [slideIdx, setSlideIdx] = useState(0);
  // Track which slide indices have been "reached" so we only mount those
  // <img> elements. This avoids downloading all 13 Facebook-CDN slides on
  // initial page load — the browser now fetches only the current + next
  // slide, saving hundreds of KB on first paint.
  const [loadedSlides, setLoadedSlides] = useState(() => new Set([0]));

  // Auto-cycle images when there is no video and 2+ slides
  useEffect(() => {
    if (videoKind) return;
    if (slides.length < 2) return;
    const t = setInterval(() => {
      setSlideIdx((i) => {
        const next = (i + 1) % slides.length;
        // Preload one slide ahead so the crossfade never blocks on network.
        setLoadedSlides((s) => new Set(s).add(next).add((next + 1) % slides.length));
        return next;
      });
    }, SLIDE_INTERVAL_MS);
    return () => clearInterval(t);
  }, [videoKind, slides.length]);

  // Preload slide[1] as soon as the component mounts (so the very first
  // transition has an image ready).
  useEffect(() => {
    if (slides.length >= 2) {
      setLoadedSlides((s) => new Set(s).add(1));
    }
  }, [slides.length]);

  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  const heading1 = hero.heading_1 || "Built on Honesty.";
  const heading2 = hero.heading_2 || "Backed by Integrity.";
  const heading3 = (hero.heading_3 || "").trim();

  return (
    <section id="home" data-testid="hero-section" className="relative min-h-[92vh] flex items-center overflow-hidden bg-[#121212]">
      {/* MEDIA LAYER */}
      {videoKind === "mp4" && (
        <video
          key={hero.video_url}
          src={hero.video_url}
          data-testid="hero-background-video"
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          className="absolute inset-0 w-full h-full object-cover"
        />
      )}

      {videoKind === "youtube" && (
        <div className="absolute inset-0 w-full h-full overflow-hidden pointer-events-none" data-testid="hero-background-youtube">
          {/* Sized 16:9 to cover the viewport regardless of aspect */}
          <iframe
            title="Hero background"
            src={youtubeEmbedUrl(hero.video_url, { background: true })}
            allow="autoplay; encrypted-media"
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 min-w-[177.77vh] min-h-[100vh] w-[177.77vh] h-[100vh] max-w-none border-0 pointer-events-none"
          />
        </div>
      )}

      {!videoKind && (
        <>
          {slides.map((s, i) => {
            const src = s.image_data || s.image_url || DEFAULT_HERO_BG;
            // Only actually render the <img> once the slide has been "reached"
            // in the cycle. Others stay as empty divs so the crossfade layout
            // is stable but no download is triggered.
            if (!loadedSlides.has(i)) {
              return (
                <div
                  key={`ph-${i}`}
                  data-testid={`hero-slide-placeholder-${i}`}
                  className="absolute inset-0 w-full h-full opacity-0"
                  aria-hidden="true"
                />
              );
            }
            return (
              <img
                key={`${i}-${src?.slice?.(0, 40)}`}
                src={src}
                alt="LC Builders project"
                data-testid={`hero-slide-image-${i}`}
                loading={i === 0 ? "eager" : "lazy"}
                fetchpriority={i === 0 ? "high" : "auto"}
                decoding="async"
                className={`absolute inset-0 w-full h-full object-cover ken-burns transition-opacity duration-[1200ms] ${
                  i === slideIdx ? "opacity-100" : "opacity-0"
                }`}
              />
            );
          })}
        </>
      )}

      {/* Overlays */}
      <div className="absolute inset-0 bg-black/60 pointer-events-none" />
      <div className="absolute inset-0 bg-gradient-to-b from-[#121212]/40 via-[#121212]/20 to-[#121212] pointer-events-none" />
      <div className="absolute left-0 top-0 h-full w-2 stripe-accent hidden md:block" />

      {/* Content */}
      <div className="relative max-w-7xl mx-auto px-6 md:px-12 w-full py-24 md:py-32">
        <div className="max-w-4xl animate-fade-up">
          {hero.eyebrow && (
            <div className="inline-flex items-center gap-3 mb-8 border border-white/20 px-4 py-2">
              <span className="w-2 h-2 bg-[#84FB00]" />
              <span className="font-display tracking-[0.3em] text-xs text-white/80">{hero.eyebrow}</span>
            </div>
          )}

          <h1
            data-testid="hero-heading"
            className={`font-display uppercase leading-[0.9] tracking-tight text-white ${
              heading3
                ? "text-5xl sm:text-6xl md:text-7xl"
                : "text-6xl sm:text-7xl md:text-8xl"
            }`}
          >
            {heading1}<br />
            <span className="text-[#4AB300]">{heading2}</span>
            {heading3 && (
              <>
                <br />
                <span className="text-white/70">{heading3}</span>
              </>
            )}
          </h1>

          <p className="mt-8 max-w-2xl text-lg md:text-xl text-white/80 leading-relaxed">{hero.subtitle}</p>

          <div className="mt-12 flex flex-col sm:flex-row gap-4">
            <button
              data-testid="hero-estimate-button"
              onClick={() => scrollTo("contact")}
              className="group inline-flex items-center justify-center gap-3 bg-[#84FB00] hover:bg-[#6EE500] transition-colors px-8 py-5 font-display tracking-widest uppercase text-white"
            >
              Get Free Estimate
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </button>
            <a
              data-testid="hero-call-button"
              href={`tel:${phoneLink}`}
              className="inline-flex items-center justify-center gap-3 bg-transparent border border-white/30 hover:border-[#84FB00] hover:text-[#4AB300] transition-colors px-8 py-5 font-display tracking-widest uppercase text-white"
            >
              <Phone size={16} /> Call {phone}
            </a>
            {facebookUrl && (
              <a
                data-testid="hero-facebook-button"
                href={facebookUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-3 bg-[#1877F2] hover:bg-[#0F5CBF] transition-colors px-8 py-5 font-display tracking-widest uppercase text-white"
              >
                <Facebook size={16} /> Check Us Out on Facebook
              </a>
            )}
          </div>

          {Number(contact.google_review_count) > 0 && (
            <a
              data-testid="hero-google-rating-badge"
              href={(contact.review_url || contact.google_profile_url || "").trim() || "#"}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-8 inline-flex items-center gap-3 border border-white/20 hover:border-[#84FB00] bg-black/30 backdrop-blur-sm px-5 py-3 transition-colors group w-fit"
            >
              {/* Multicolor Google G */}
              <svg width="20" height="20" viewBox="0 0 48 48" aria-hidden="true" className="flex-shrink-0">
                <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.1 8.1 3l5.7-5.7C34.9 6.1 29.7 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/>
                <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.6 15.7 18.9 12 24 12c3.1 0 5.9 1.1 8.1 3l5.7-5.7C34.9 6.1 29.7 4 24 4 16 4 9.1 8.7 6.3 14.7z"/>
                <path fill="#4CAF50" d="M24 44c5.7 0 10.7-2 14.3-5.2l-6.6-5.6C29.6 34.6 26.9 36 24 36c-5.2 0-9.6-3.3-11.2-7.9L6.2 33C9 39.1 16 44 24 44z"/>
                <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4 5.6l6.6 5.6C41.8 36.8 44 30.9 44 24c0-1.3-.1-2.4-.4-3.5z"/>
              </svg>
              <span className="font-display text-white text-lg leading-none">
                {(Number(contact.google_rating) || 5).toFixed(1)}
              </span>
              <div className="flex gap-0.5" aria-hidden="true">
                {Array.from({ length: 5 }).map((_, i) => {
                  const filled = i < Math.round(Number(contact.google_rating) || 5);
                  return (
                    <svg key={i} width="14" height="14" viewBox="0 0 24 24" className="flex-shrink-0">
                      <path
                        fill={filled ? "#FFD700" : "#4A4A4A"}
                        d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"
                      />
                    </svg>
                  );
                })}
              </div>
              <span className="text-white/70 text-sm tracking-widest uppercase group-hover:text-[#4AB300] transition-colors">
                {Number(contact.google_review_count)} Google Review{Number(contact.google_review_count) === 1 ? "" : "s"}
              </span>
            </a>
          )}

          {hero.stats?.length > 0 && (
            <div className="mt-16 grid grid-cols-3 gap-6 md:gap-12 max-w-2xl border-t border-white/10 pt-8">
              {hero.stats.map((s, i) => (
                <div key={i} data-testid={`hero-stat-${i}`}>
                  <div className="font-display text-4xl md:text-5xl text-[#4AB300] leading-none">{s.n}</div>
                  <div className="text-xs md:text-sm text-white/60 mt-2 tracking-widest uppercase">{s.l}</div>
                </div>
              ))}
            </div>
          )}

          {/* Slideshow indicator dots */}
          {!videoKind && slides.length > 1 && (
            <div className="mt-10 flex items-center gap-2" data-testid="hero-slide-dots">
              {slides.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  aria-label={`Go to slide ${i + 1}`}
                  onClick={() => setSlideIdx(i)}
                  className={`h-1 transition-all ${i === slideIdx ? "w-10 bg-[#84FB00]" : "w-6 bg-white/30 hover:bg-white/50"}`}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
